import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { Loader2 } from 'lucide-react';
interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}
export function Button({
  className = '',
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0B1426] disabled:opacity-50 disabled:pointer-events-none relative overflow-hidden group';
  const variants = {
    primary: 'bg-[#d8a43e] text-black hover:bg-[#e5b54e] focus:ring-[#d8a43e] shadow-[0_0_20px_rgba(216,164,62,0.3)] hover:shadow-[0_0_30px_rgba(216,164,62,0.5)]',
    secondary: 'bg-white/10 backdrop-blur-md text-white hover:bg-white/20 focus:ring-white/50 border border-white/10',
    outline: 'border border-[#d8a43e] text-[#d8a43e] hover:bg-[#d8a43e]/10 focus:ring-[#d8a43e]',
    ghost: 'text-gray-400 hover:bg-white/5 hover:text-white focus:ring-white/20'
  };
  const sizes = {
    sm: 'h-9 px-3 text-sm',
    md: 'h-11 px-6 text-base',
    lg: 'h-14 px-8 text-lg'
  };
  return <motion.button whileTap={{
    scale: 0.98
  }} whileHover={{
    scale: 1.02
  }} className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`} disabled={disabled || isLoading} {...props} data-id="element-292">
      {/* Tech scanline effect for primary buttons */}
      {variant === 'primary' && <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-293" />}

      {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-294" />}
      {!isLoading && leftIcon && <span className="mr-2" data-id="element-295">{leftIcon}</span>}
      <span className="relative z-10 font-semibold tracking-wide" data-id="element-296">
        {children}
      </span>
      {!isLoading && rightIcon && <span className="ml-2" data-id="element-297">{rightIcon}</span>}
    </motion.button>;
}